/**
 * La preferenza "in card" di un utente, per **modello** e in **localStorage**.
 *
 * Non e' un'impostazione del processo: un processo non decide cosa si vede in
 * card, lo decide il modello che lo definisce, e chi lo modifica esprime
 * un'eccezione personale. Per questo la chiave e' `(utente, organizzazione,
 * modello)` e non contiene il processo: i processi di quel modello ereditano il
 * default del modello, e l'eccezione segue la persona.
 *
 * Si salva **solo lo scostamento**: un campo lasciato al valore standard del
 * modello non produce nessuna voce. Cosi' un modello che cambia il proprio
 * default non resta inchiodato a una scelta che nessuno ha mai fatto davvero —
 * chi non ha toccato niente segue il modello, chi lo ha toccato porta la sua
 * eccezione (`withCardPreference`).
 *
 * Dove vive cosa: il `localStorage` e' la persistenza — e' cio' che si scrive e
 * da cui si ripristina. La `Map` in memoria e' solo l'**istantanea** che legge
 * `readCardPreferences`: un riferimento stabile, perche' `useSyncExternalStore`
 * confronta per identita' e riparsare il JSON a ogni render rimonterebbe i
 * consumatori a vuoto.
 */

export type CardPreferences = Readonly<Record<string, boolean>>;

/** Il default del modello quando non c'e' nessuno scostamento da leggere. */
const EMPTY: CardPreferences = {};

const preferences = new Map<string, CardPreferences>();
const listeners = new Set<() => void>();

const STORAGE_PREFIX = "cf-incard:";
const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`;

/**
 * La chiave della preferenza: `(utente, organizzazione, modello)`.
 *
 * Il terzo pezzo era il processo; ora e' il modello. Stessa forma, sorgente
 * diversa.
 */
export const cardPreferenceKey = (userId: number, organizationId: number, modelId: string | number) =>
  `${userId}:${organizationId}:${modelId}`;

export const readCardPreferences = (key: string) => preferences.get(key);

export const subscribeCardPreferences = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

const notify = () => listeners.forEach(listener => listener());

/**
 * Lo scostamento, applicato a una mappa.
 *
 * Pura di proposito: e' qui che si decide se una voce va tolta. Un valore uguale
 * al default del modello **cancella** la voce invece di scriverla — cosi' la
 * mappa resta quella dei soli scostamenti, e un'assenza si legge come "segui il
 * modello".
 */
export function withCardPreference(
  current: CardPreferences,
  fieldId: string,
  inCard: boolean,
  modelDefault: boolean
): CardPreferences {
  const next: Record<string, boolean> = { ...current };
  if (inCard === modelDefault) delete next[fieldId];
  else next[fieldId] = inCard;
  return next;
}

const readStored = (key: string): CardPreferences => {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as CardPreferences)
      : EMPTY;
  } catch {
    // localStorage assente o JSON corrotto: nessuno scostamento e' il default
    // giusto, il modello copre il resto.
    return EMPTY;
  }
};

const writeStored = (key: string, prefs: CardPreferences) => {
  try {
    if (Object.keys(prefs).length === 0) window.localStorage.removeItem(storageKey(key));
    else window.localStorage.setItem(storageKey(key), JSON.stringify(prefs));
  } catch {
    // Quota piena o modalita' privata: la scelta resta in memoria per la
    // sessione, non si perde la UI per un errore di scrittura.
  }
};

export function clearCardPreferences() {
  preferences.clear();
  notify();
}

/**
 * Ripristina dalla memoria locale le preferenze dei modelli indicati.
 *
 * Non c'e' piu' rete: nessuna chiamata, nessun caso di errore da riportare. La
 * firma resta `async` solo perche' i chiamanti la attendono.
 */
export async function loadCardPreferences(
  userId: number, organizationId: number, modelIds: number[]
): Promise<boolean> {
  const ids = Array.from(new Set(modelIds)).filter(id => Number.isSafeInteger(id) && id > 0);
  for (const id of ids) {
    const key = cardPreferenceKey(userId, organizationId, id);
    // `has` e non set incondizionato: un'istantanea nuova a ogni reload
    // rimonterebbe i consumatori anche quando nulla e' cambiato.
    if (!preferences.has(key)) preferences.set(key, readStored(key));
  }
  notify();
  return true;
}

/**
 * Scrive lo scostamento di un campo, o lo cancella se coincide col default.
 *
 * `modelDefault` e' il valore standard del modello per quel campo
 * (`field.definition.inCard`): e' il chiamante ad averlo, ed e' l'unico modo per
 * distinguere "scelta" da "default".
 */
export function saveCardPreference(
  userId: number, organizationId: number, modelId: string | number,
  fieldId: string, inCard: boolean, modelDefault: boolean
) {
  const key = cardPreferenceKey(userId, organizationId, modelId);
  const next = withCardPreference(preferences.get(key) ?? EMPTY, fieldId, inCard, modelDefault);
  preferences.set(key, next);
  writeStored(key, next);
  notify();
}
